// src/views/admin/clients/index.jsx
import { Button, useToast } from '@chakra-ui/react';
import ClientsTable from '../../../components/Clients/ClientsTable.js';
import { columnsDataClients } from './variables/columnsDataClients.js';
import React, { useEffect, useState } from 'react';
import { getData, postData, putData, deleteData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext';
import ClientsEditModal from '../../../components/Clients/ClientsEditModal.js';
import DeleteModal from '../../../components/DeleteModal/DeleteModal.js';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import { useResetPageOnFilterChange } from '../../../hooks/useResetPageOnFilterChange.js';
import { useTableSorting } from '../../../hooks/useTableSorting.js';
import authService from '../../../services/authService.js';

export default function Settings() {
  const isReadOnly = authService.isMechanic();
  const canDeleteClients = authService.hasPermission('clients.delete');
  const { searchQuery, resetListFilters } = useSearch(); 
  const [data, setData] = useState([]); 
  const [page, setPage] = useState(0); 
  const [totalPages, setTotalPages] = useState(0); 
  const [modalState, setModalState] = useState({ isOpen: false, customer: null, isNew: false });
  const [deleteModalState, setDeleteModalState] = useState({ isOpen: false, customer: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10); 
  const toast = useToast();
  const { sorting, setSorting, sortBy, sortDir, appendSortParams } = useTableSorting();

  useResetPageOnFilterChange(setPage, page, [searchQuery, rowsPerPage, sortBy, sortDir]);

  const loadData = async (dato) => {
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        data: dato || '',
      });
      appendSortParams(params);
      const response = await getData(`/customers?${params.toString()}`);
      setData(response?.payload?.customers || []); // Actualiza los datos
      setTotalPages(Math.ceil(response?.payload?.total / rowsPerPage));
    } catch (error) {
      toast({
        title: 'Error al obtener clientes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    loadData(searchQuery);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, rowsPerPage, sortBy, sortDir]);

  useEffect(() => {
    resetListFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const openNewCustomerModal = () => {
    setModalState({ isOpen: true, customer: { id: null, name: '' }, isNew: true });
  };

  const closeEditModal = () => {
    setModalState({ isOpen: false, customer: null, isNew: false });
  };

  const handleCreateCustomer = async (newCustomer) => {
    try {
      await postData('/customers', newCustomer);
      loadData(searchQuery);
      closeEditModal(); 
      toast({
        title: 'Cliente creado exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error al crear cliente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateCustomer = async (customer) => {
    try {
      await putData(`/customers/${customer.id}`, customer);
      await loadData(searchQuery); 
      closeEditModal(); 
      toast({
        title: 'Cliente actualizado exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error al actualizar el cliente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openDeleteModal = (customer) => {
    setDeleteModalState({ isOpen: true, customer });
  };

  const closeDeleteModal = () => {
    setDeleteModalState({ isOpen: false, customer: null });
  };

  const handleDeleteCustomer = async () => {
    const customer = deleteModalState.customer;
    if (!customer?.id) return;
    try {
      setIsDeleting(true);
      await deleteData(`/customers/${customer.id}`);
      await loadData(searchQuery);
      closeDeleteModal();
      toast({
        title: 'Cliente eliminado',
        description: 'El cliente se eliminó correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'No se pudo eliminar el cliente',
        description: error.message || 'Error del servidor',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const columnsData = columnsDataClients.map((column) => {
    if (column.accessor !== 'actions') return column;
    let actions = column.actions;
    if (isReadOnly) {
      actions = actions.filter((action) => action.type === 'view');
    } else if (!canDeleteClients) {
      actions = actions.filter((action) => action.type !== 'delete');
    }
    return { ...column, actions };
  });

  return (
    <AdminPageShell
      title="Clientes"
      actions={!isReadOnly ? (
        <Button variant="adminPrimary" size="sm" onClick={openNewCustomerModal}>
          Nuevo cliente
        </Button>
      ) : null}
    >
      <ClientsTable
        columnsData={columnsData}
        data={data}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
          pageSize={rowsPerPage}
          setPageSize={setRowsPerPage}
        handleUpdateCustomer={handleUpdateCustomer}
        onDeleteCustomer={canDeleteClients && !isReadOnly ? openDeleteModal : undefined}
        sorting={sorting}
        onSortingChange={setSorting}
      />
      {!isReadOnly && (
        <ClientsEditModal
          isOpen={modalState.isOpen}
          onClose={closeEditModal}
          vehicle={modalState.customer}
          isNew={modalState.isNew}
          onCreate={handleCreateCustomer}
        />
      )}
      {canDeleteClients && !isReadOnly && (
        <DeleteModal
          isOpen={deleteModalState.isOpen}
          onClose={closeDeleteModal}
          onConfirm={handleDeleteCustomer}
          name={deleteModalState.customer?.name}
          isLoading={isDeleting}
        />
      )}
    </AdminPageShell>
  );
}
